/**
 * Who you are, and how you stay that way across reloads.
 *
 * Two kinds of user, by design:
 *
 * - a **guest**, authenticated by a device id, who never sees a password and
 *   can play forever;
 * - an **account**, authenticated by email, who can own games.
 *
 * The rule that matters most in this file: **a guest who registers keeps their
 * user id.** Nakama can either create a fresh account or attach an email to the
 * existing one, and the difference is invisible at the moment it happens and
 * expensive weeks later — the wrong choice silently strands their profile and
 * anything they were entitled to. So registration LINKS when a session already
 * exists, and only creates when there is nothing to keep.
 */

import { Session } from '@heroiclabs/nakama-js';
import { client, httpBase, httpKey } from './client.js';

const DEVICE_KEY = 'cards.deviceId';
const SESSION_KEY = 'cards.session';

/** localStorage throws in some privacy modes; never let that break a sign-in. */
function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private browsing: the session simply will not survive a reload */
  }
}

function clearLocal(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* see above */
  }
}

/**
 * The `?as=` label, when the address bar carried one.
 *
 * Its whole job is to let several players be driven from one browser, which is
 * the only practical way to test a four-handed game — or a voice call, which
 * needs two people before there is anything to hear — on one machine.
 */
function asLabel(): string | null {
  return new URLSearchParams(window.location.search).get('as');
}

/**
 * Where this tab's session is kept.
 *
 * A labelled tab gets its **own** key. Sharing one would mean every `?as=` tab
 * overwriting the last one's session — and overwriting the real visitor's on
 * the way past, so testing four hands would sign the tester out of their own
 * account. Separate keys also mean a labelled tab survives a reload as the
 * same player, which is what you want halfway through a hand.
 */
function sessionKey(): string {
  const label = asLabel();
  return label ? `${SESSION_KEY}.${label}` : SESSION_KEY;
}

/**
 * A stable per-browser id.
 *
 * `?as=` overrides it. It grants nothing — clearing localStorage has the same
 * effect, and Nakama still issues the session.
 */
function deviceId(): string {
  const override = asLabel();
  // Nakama requires 10-128 characters, so short labels get padded.
  if (override) return `devtest-${override}`.padEnd(12, '0');

  const existing = readLocal(DEVICE_KEY);
  if (existing) return existing;

  const fresh =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `dev-${String(Date.now())}-${Math.random().toString(36).slice(2, 10)}`;
  writeLocal(DEVICE_KEY, fresh);
  return fresh;
}

function remember(session: Session): Session {
  writeLocal(
    sessionKey(),
    JSON.stringify({ token: session.token, refreshToken: session.refresh_token }),
  );
  return session;
}

/**
 * Sign out of an account and return to being a guest.
 *
 * The device id is deliberately kept: signing out of an account should not
 * also throw away the identity that lets you rejoin the table you are sitting
 * at. It is a sign-out, not an amnesia.
 */
export async function signOut(): Promise<Session> {
  clearLocal(sessionKey());
  return signInAsGuest();
}

/**
 * Bring back the session from a previous visit, refreshing it if it has aged out.
 *
 * Returns null when there is nothing to restore or the refresh token has also
 * expired — the caller then decides between a guest sign-in and a login screen.
 */
export async function restoreSession(): Promise<Session | null> {
  const stored = readLocal(sessionKey());
  if (!stored) return null;

  try {
    const { token, refreshToken } = JSON.parse(stored) as {
      token?: string;
      refreshToken?: string;
    };
    if (!token || !refreshToken) return null;

    const session = Session.restore(token, refreshToken);
    if (!session.isexpired(Date.now() / 1000)) return session;

    // Expired but refreshable. If the refresh token has gone too, this throws
    // and we fall through to a clean slate rather than a broken session.
    return remember(await client.sessionRefresh(session));
  } catch {
    // Unusable tokens are worse than none: clear them so the next attempt
    // starts clean rather than failing the same way forever.
    clearLocal(sessionKey());
    return null;
  }
}

export async function signInAsGuest(): Promise<Session> {
  return remember(await client.authenticateDevice(deviceId(), true));
}

export async function signInWithEmail(email: string, password: string): Promise<Session> {
  // create: false — signing in must never quietly create an account for a
  // mistyped address, which would then look like "my password stopped working".
  return remember(await client.authenticateEmail(email.trim(), password, false));
}

/**
 * Register, keeping whatever the person already has.
 *
 * With a session in hand this **links** the email to it, so a guest who has been
 * playing all evening keeps their id, their name and their entitlements. Only a
 * genuinely new visitor gets a fresh account.
 */
export async function registerWithEmail(
  email: string,
  password: string,
  existing: Session | null,
): Promise<Session> {
  const address = email.trim();

  if (existing) {
    await client.linkEmail(existing, { email: address, password });
    // The tokens do not change on a link, but the account behind them now has
    // an email — so the stored session is still correct and still ours.
    return remember(existing);
  }

  return remember(await client.authenticateEmail(address, password, true));
}

export function requestPasswordReset(email: string): Promise<{ ok: boolean; message?: string }> {
  return unauthenticatedRpc('request_password_reset', { email: email.trim() });
}

export function redeemPasswordReset(
  token: string,
  password: string,
): Promise<{ ok: boolean; message?: string }> {
  return unauthenticatedRpc('redeem_password_reset', { token, password });
}

/**
 * An RPC with no session.
 *
 * Password reset is for people who cannot sign in, so it cannot require a
 * session. Nakama accepts an `http_key` for exactly this, and the SDK's rpc
 * helper always wants a session — hence the direct call.
 */
async function unauthenticatedRpc(
  name: string,
  input: unknown,
): Promise<{ ok: boolean; message?: string }> {
  const response = await fetch(`${httpBase}/v2/rpc/${name}?http_key=${httpKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Nakama expects the payload as a JSON *string*, so it is encoded twice.
    body: JSON.stringify(JSON.stringify(input)),
  });

  const body = (await response.json()) as { payload?: string };
  return JSON.parse(body.payload ?? '{}') as { ok: boolean; message?: string };
}
